import { useEffect, useMemo, useState } from 'react';
import { Chip, Popover, Box, Typography } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { PrimitiveType } from '../../../../api/api';
import { codeFontFamily } from '../../../../themes/codeTokens';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import VarPathSelectTree from './VarPathSelectTree';

type VarPathFieldProps = {
	matchingPrimitiveTypes?: PrimitiveType[];
	value: string[] | undefined;
	onValueChange?: (value: string[]) => void;
	variableScope: IVariableScope | undefined;
	placeholder?: string;
	disabled?: boolean;
};

export default function VarPathField({
	matchingPrimitiveTypes,
	value,
	onValueChange,
	variableScope,
	placeholder = 'Select variable',
	disabled = false,
}: VarPathFieldProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation(); // Prevent bubbling to parent components
		if (!disabled) {
			setAnchorEl(event.currentTarget as any);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleSelectionComplete = () => {
		// Close popover when selection is made in the tree
		handleClose();
	};

	useEffect(() => {
		// Close popover when variable scope changes to ensure fresh content
		setAnchorEl(null);
	}, [variableScope]);

	const open = Boolean(anchorEl);
	const id = open ? 'var-path-popover' : undefined;

	// Display value as a readable path
	const displayValue = useMemo(() => {
		if (value && value.length > 0) {
			return value.join('.');
		}
		return placeholder;
	}, [value, placeholder]);
	return (
		<div>
			<Chip
				aria-describedby={id}
				variant="outlined"
				onClick={handleClick}
				disabled={disabled}
				deleteIcon={<ArrowDropDown />}
				color="info"
				label={displayValue}
				sx={{
					fontFamily: value?.length ? codeFontFamily : undefined,
					justifyContent: 'space-between',
					maxWidth: '100%',
					'& .MuiChip-deleteIcon': {
						color: 'inherit',
					},
				}}
			/>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				onClick={(e) => e.stopPropagation()}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Box sx={{ p: 2 }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Select Variable
					</Typography>
					{!variableScope && (
						<Typography variant="body2" color="text.secondary">
							No variable scope available
						</Typography>
					)}
					{variableScope && (
						<>
						{variableScope.getDiagnostics?.().map((message) => (
							<Typography key={message} variant="body2" color="warning.main" sx={{ mb: 1 }}>{message}</Typography>
						))}
						<VarPathSelectTree
							matchingPrimitiveTypes={matchingPrimitiveTypes}
							value={value}
							onValueChange={onValueChange}
							variableScope={variableScope}
							onSelectionComplete={handleSelectionComplete}
						/>
						</>
					)}
				</Box>
			</Popover>
		</div>
	);
}
